import { print } from 'graphql';
import { addTypenameToDocument } from '@apollo/client/utilities';
import { gql as gqlTag } from 'graphql-tag';
import cloneDeep from 'lodash/cloneDeep';
import DotObject from 'dot-object';
import api from './api';

export class GraphQLClient {
  constructor(config) {
    this.config = config;
    this.data = {};
    this.dataSubscribers = [];
  }

  async query(query, variables, callback) {
    return await this.sendGraphQLRequest(query, variables, callback);
  }

  async sendGraphQLRequest(query, variables, callback) {
    if (callback) {
      this.dataSubscribers.push(callback);
    }

    const url = api.url('/graphql');
    const headers = {
      'Content-Type': 'application/json',
      Bearer: api.bearer()
    };

    const options = {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query: print(addTypenameToDocument(query)),
        variables: variables || {}
      })
    };

    const response = await fetch(url, options);

    if (!response) {
      throw new Error('Request Timeout');
    }

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const contentType = response.headers.get('Content-Type');

    if (contentType && contentType.includes('multipart/mixed')) {
      await this.processMultipartResponse(response);
    } else {
      await this.processJSONResponse(response);
    }

    return this.data;
  }

  async processMultipartResponse(response) {
    const reader = response.body.getReader();

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      const chunk = new TextDecoder('utf-8').decode(value);
      const parts = chunk.split('\r\n---\r\n');

      for (const part of parts) {
        if (part) {
          if (this.handleStreamedResponsePart(part) === false) {
            break;
          }
        }
      }
    }
  }

  async processJSONResponse(response) {
    const responseData = await response.json();

    if (responseData.errors) {
      throw new Error('Received error:', responseData.errors);
    }

    if (responseData.data) {
      this.data = responseData.data;
    }
  }

  handleStreamedResponsePart(part) {
    const headersAndData = part.split('\r\n\r\n');
    const headersText = headersAndData[0];
    const dataText = headersAndData[1];

    if (dataText === '-----') {
      return false;
    }

    const headersLines = headersText.split('\r\n');
    const headersObj = {};

    for (const line of headersLines) {
      const [key, value] = line.split(': ');
      headersObj[key] = value;
    }

    if (headersObj['Content-Type'] === 'application/json') {
      const data = JSON.parse(dataText);

      if (data.errors) {
        throw new Error('Received error:', data.errors);
      }

      this.insertChunkIntoData(data);
    }
  }

  insertChunkIntoData(chunk) {
    if (!chunk.path) {
      this.mutateData(chunk.data);
    } else {
      const path = chunk.path.join('.');
      const current = cloneDeep(this.data);

      DotObject.set(path, chunk.data, current);

      this.mutateData(current);
    }
  }

  mutateData(newData) {
    const oldData = this.data;

    this.dataSubscribers.forEach((callback) => {
      callback(newData, oldData);
    });

    this.data = newData;
  }
}

export const gqlClient = new GraphQLClient();
export const gql = gqlTag;
